import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

const TEAL = '#0d9488';
const TEAL_DARK = '#0f766e';

const thSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
  bgcolor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  py: 1.25
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'warning' },
  { value: 'in_progress', label: 'In progress', color: 'info' },
  { value: 'resolved', label: 'Resolved', color: 'success' },
  { value: 'closed', label: 'Closed', color: 'default' }
];

function statusChip(status) {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || { label: status, color: 'default' };
  return <Chip size="small" label={opt.label} color={opt.color} variant={status === 'open' ? 'filled' : 'outlined'} />;
}

function targetLabel(row) {
  if (row.target_type === 'hr') return 'HR / Company';
  return row.manager_name ? `Manager: ${row.manager_name}` : 'Manager';
}

export default function StaffingGrievancesTab({ apiRequest, onFeedback }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState('open');
  const [editResponse, setEditResponse] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (targetFilter) params.set('target_type', targetFilter);
      const q = params.toString() ? `?${params}` : '';
      const data = await apiRequest(`/api/company/hrms/grievances${q}`);
      setRows(data.grievances || []);
    } catch (e) {
      onFeedback(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, statusFilter, targetFilter, onFeedback]);

  useEffect(() => {
    load();
  }, [load]);

  const openCount = useMemo(() => rows.filter((r) => r.status === 'open' || r.status === 'in_progress').length, [rows]);

  const openDetail = (row) => {
    setSelected(row);
    setEditStatus(row.status);
    setEditResponse(row.company_response || '');
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiRequest(`/api/company/hrms/grievances/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: editStatus, company_response: editResponse })
      });
      onFeedback('Grievance updated.');
      setSelected(null);
      await load();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0f172a">
            Grievance center
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review grievances raised by employees against HR or their reporting manager.
          </Typography>
        </Box>
        {openCount > 0 && <Chip label={`${openCount} active`} color="warning" />}
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Raised against"
          value={targetFilter}
          onChange={(e) => setTargetFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="hr">HR / Company</MenuItem>
          <MenuItem value="manager">Manager</MenuItem>
        </TextField>
        <Button size="small" variant="outlined" onClick={load}>
          Refresh
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Typography sx={{ p: 2 }} color="text.secondary">
              Loading…
            </Typography>
          ) : rows.length === 0 ? (
            <Typography sx={{ p: 2 }} color="text.secondary">
              No grievances match your filters.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, border: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>Employee</TableCell>
                    <TableCell sx={thSx}>Against</TableCell>
                    <TableCell sx={thSx}>Subject</TableCell>
                    <TableCell sx={thSx}>Status</TableCell>
                    <TableCell sx={thSx}>Submitted</TableCell>
                    <TableCell sx={thSx} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {r.employee_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.employee_email || r.employee_mobile || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={targetLabel(r)} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {r.subject}
                        </Typography>
                      </TableCell>
                      <TableCell>{statusChip(r.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" onClick={() => openDetail(r)} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                          Manage
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Grievance #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Employee
                </Typography>
                <Typography fontWeight={700}>{selected.employee_name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Raised against
                </Typography>
                <Typography>{targetLabel(selected)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Subject
                </Typography>
                <Typography fontWeight={600}>{selected.subject}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selected.description}</Typography>
              </Box>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Response to employee"
                placeholder="Your reply or resolution notes (visible to the employee)"
                value={editResponse}
                onChange={(e) => setEditResponse(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={save} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
